import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Pencil, Trash2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/adminApi";
import { formatDate } from "@/pages/Blog";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-[hsl(var(--secondary))]/40 border border-border rounded-xl p-8 space-y-4">
        <h1 className="font-display text-2xl">Admin login</h1>
        <p className="text-sm text-muted-foreground">Sign in to manage blog posts.</p>
        <div>
          <label className="block text-sm font-medium mb-2">Email</label>
          <input type="email" required data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Password</label>
          <input type="password" required data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        </div>
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[#1A3A31] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function PostRow({ post, onEdit, onTogglePublish, onDelete }) {
  return (
    <div data-testid={`admin-post-row-${post.slug}`} className="flex items-center gap-4 p-4 border-b border-border last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="font-display text-base truncate">{post.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{post.category} · {formatDate(post.created_at)}</p>
      </div>
      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${post.published ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] text-muted-foreground"}`}>
        {post.published ? "Published" : "Draft"}
      </span>
      <button data-testid={`admin-toggle-publish-${post.slug}`} onClick={() => onTogglePublish(post)} title={post.published ? "Unpublish" : "Publish"}
        className="p-2 rounded-md border border-border hover:bg-[hsl(var(--secondary))]">
        {post.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
      <button data-testid={`admin-edit-${post.slug}`} onClick={() => onEdit(post)} title="Edit"
        className="p-2 rounded-md border border-border hover:bg-[hsl(var(--secondary))]">
        <Pencil className="h-4 w-4" />
      </button>
      <button data-testid={`admin-delete-${post.slug}`} onClick={() => onDelete(post)} title="Delete"
        className="p-2 rounded-md border border-border text-red-400 hover:bg-red-500/10">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

const EMPTY_POST = { title: "", category: "", excerpt: "", cover_image: "", content: "", published: false };

function inputCls() {
  return "w-full bg-transparent border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";
}

function PostEditor({ post, onBack, onSaved }) {
  const [form, setForm] = useState(post ? { ...EMPTY_POST, ...post } : EMPTY_POST);
  const [busy, setBusy] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title, category: form.category, excerpt: form.excerpt,
        content: form.content, cover_image: form.cover_image || null, published: form.published,
      };
      if (post?.id) {
        await api.put(`/admin/posts/${post.id}`, payload);
      } else {
        await api.post("/admin/posts", payload);
      }
      toast.success("Post saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-5" data-testid="admin-post-editor">
      <button type="button" onClick={onBack} data-testid="admin-editor-back"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to posts
      </button>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium mb-2">Title *</label>
          <input required data-testid="admin-field-title" value={form.title} onChange={setField("title")} className={inputCls()} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Category *</label>
          <input required data-testid="admin-field-category" value={form.category} onChange={setField("category")} className={inputCls()} placeholder="e.g. Hiring Trends" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Excerpt * <span className="text-muted-foreground font-normal">(shown on listing cards)</span></label>
        <textarea required rows={2} data-testid="admin-field-excerpt" value={form.excerpt} onChange={setField("excerpt")} className={inputCls()} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Cover image URL</label>
        <input data-testid="admin-field-cover" value={form.cover_image || ""} onChange={setField("cover_image")} className={inputCls()} placeholder="https://..." />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Content * <span className="text-muted-foreground font-normal">(Markdown supported: ## headings, **bold**, lists, [links](/contact))</span></label>
        <textarea required rows={16} data-testid="admin-field-content" value={form.content} onChange={setField("content")} className={`${inputCls()} font-mono text-xs`} />
      </div>
      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="admin-field-published" checked={form.published}
            onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="h-4 w-4 accent-[hsl(var(--primary))]" />
          Published
        </label>
        <button type="submit" disabled={busy} data-testid="admin-save-post"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[#1A3A31] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save post"}
        </button>
      </div>
    </form>
  );
}

function Dashboard({ user, onLogout }) {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=list, null=new, obj=edit

  const load = useCallback(() => {
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => toast.error("Failed to load posts"));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function openEdit(post) {
    const { data } = await api.get(`/admin/posts/${post.id}`);
    setEditing(data);
  }

  async function togglePublish(post) {
    await api.put(`/admin/posts/${post.id}`, { published: !post.published });
    toast.success(post.published ? "Post unpublished" : "Post published");
    load();
  }

  async function remove(post) {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/posts/${post.id}`);
    toast.success("Post deleted");
    load();
  }

  return (
    <div className="container-x py-12 md:py-16" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-4xl">Blog admin</h1>
          <p className="text-sm text-muted-foreground mt-1">Signed in as {user.email}</p>
        </div>
        <div className="flex items-center gap-3">
          {editing === undefined && (
            <button data-testid="admin-new-post" onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[#1A3A31]">
              <Plus className="h-4 w-4" /> New post
            </button>
          )}
          <button data-testid="admin-logout" onClick={onLogout}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-[hsl(var(--secondary))]">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      {editing !== undefined ? (
        <PostEditor post={editing} onBack={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      ) : posts === null ? (
        <p className="text-muted-foreground">Loading posts...</p>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden bg-[hsl(var(--secondary))]/30">
          {posts.length === 0 && <p className="p-6 text-muted-foreground">No posts yet. Create your first one.</p>}
          {posts.map((p) => (
            <PostRow key={p.id} post={p} onEdit={openEdit} onTogglePublish={togglePublish} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=authed

  useEffect(() => {
    document.title = "Admin | X-HRverse";
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    return () => { document.title = "X-HRverse | Building High-Performing Teams for the Modern Workforce"; };
  }, []);

  async function logout() {
    await api.post("/auth/logout");
    setUser(false);
  }

  if (user === null) {
    return <div className="container-x py-32 text-muted-foreground" data-testid="admin-loading">Checking session...</div>;
  }
  if (user === false) return <LoginForm onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
